import * as Query from "@tanstack/react-query"
import * as Blurhash from "./blurhash"

export async function fromBlurhash(
  blurhash: string,
  options: fromBlurhash.Options,
): Promise<string> {
  const { width, height } = options

  const pixels = await Blurhash.decode(blurhash, { width, height })

  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext("2d")!
  const imageData = new ImageData(pixels, width, height)
  ctx.putImageData(imageData, 0, 0)

  const dataUrl = canvas.toDataURL()

  return dataUrl
}

export declare namespace fromBlurhash {
  type Options = {
    width: number
    height: number
  }
}

export async function fromFile(
  file: File,
): Promise<HTMLImageElement | undefined> {
  return new Promise<HTMLImageElement | undefined>((resolve) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => resolve(undefined)
    image.src = URL.createObjectURL(file)
  })
}

export async function toBlurhash(image: HTMLImageElement): Promise<string> {
  const canvas = document.createElement("canvas")
  canvas.width = image.naturalWidth
  canvas.height = image.naturalHeight

  const ctx = canvas.getContext("2d")!
  ctx.drawImage(image, 0, 0)
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const blurhash = Blurhash.encode(data.data, {
    height: data.height,
    width: data.width,
  })

  return blurhash
}

export function useFromBlurhash(
  blurhash: string | null,
  options: useFromBlurhash.Props,
) {
  const { width, height } = options

  return Query.useQuery({
    enabled: !!blurhash && !!width && !!height,
    queryKey: ["fromBlurhash", blurhash, width, height],
    queryFn: () => {
      if (!blurhash || !width || !height) throw new Error("invalid")
      return fromBlurhash(blurhash, { width, height })
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  })
}

export declare namespace useFromBlurhash {
  type Props = {
    height: number | null
    width: number | null
  }
}
